/**
 * Presencia del kiosco: una onda de sonido, en lugar de la cara.
 *
 * Por que (decision de Ricardo, 04/08/2026): MuseTalk genera el lip-sync a
 * 2,9x tiempo real en la RTX 3060 y el resultado era "horrible" (labios a
 * trozos o llegando tarde). La cara acababa siendo un retrato quieto. Esta
 * onda dice lo mismo que tenia que decir la cara —te escucho / estoy pensando
 * / estoy hablando— sin gastar ni un ciclo de GPU, y ademas se mueve DE VERDAD
 * con la voz.
 *
 * La cara no se ha borrado: sigue tras `[avatar] modo = cara` en settings.ini,
 * lista para volver cuando haya GPU mejor.
 *
 * De donde sale el movimiento:
 *  - hablando: de la envolvente del WAV que ya viaja por el socket de voz
 *    (`reproductor.observarAudio`), sin WebAudio ni analisis en vivo;
 *  - escuchando: del nivel del microfono que mide `lib/audio.ts`;
 *  - pensando: latido lento propio, para que no parezca colgado.
 */
import { useEffect, useRef, useState } from 'react';
import { envolventeDeWav } from '@/lib/envolvente';
import * as reproductor from '@/lib/reproductor';
import { cn } from '@/lib/utils';
import type { EstadoAvatar } from '@/components/Avatar';

interface PropsOndaVoz {
  /** Que esta haciendo Androcles ahora mismo. */
  estado: EstadoAvatar;
  /** Volumen del microfono 0..1 mientras escucha. */
  nivel?: number;
  className?: string;
}

/** Barras de la onda. Impar para que haya una central bien marcada. */
const BARRAS = 21;

/** Fotogramas por segundo de la animacion (los mismos tramos que la envolvente). */
const FPS = 25;

/**
 * Reparto de altura por barra: la del centro manda y las de los lados
 * acompanan. Da forma de onda en vez de un bloque plano.
 */
const PESOS = Array.from({ length: BARRAS }, (_, i) => {
  const centro = (BARRAS - 1) / 2;
  const distancia = Math.abs(i - centro) / centro;
  return 0.25 + 0.75 * Math.cos((distancia * Math.PI) / 2) ** 1.5;
});

export function OndaVoz({ estado, nivel = 0, className }: PropsOndaVoz) {
  const [alturas, setAlturas] = useState<number[]>(() => PESOS.map(() => 0.06));
  /** Envolvente de la locucion en curso y en que tramo va. */
  const locucion = useRef<{ valores: number[]; indice: number } | null>(null);
  const nivelRef = useRef(0);
  const estadoRef = useRef<EstadoAvatar>(estado);

  useEffect(() => {
    nivelRef.current = nivel;
  }, [nivel]);

  useEffect(() => {
    estadoRef.current = estado;
    // Al dejar de hablar, la locucion pendiente se descarta: si no, la onda
    // seguiria "hablando" sola despues de callarse.
    if (estado !== 'hablando') locucion.current = null;
  }, [estado]);

  // Cada WAV que suena se convierte en su envolvente y se va consumiendo.
  useEffect(() => {
    return reproductor.observarAudio((wav) => {
      const { valores } = envolventeDeWav(wav);
      if (valores.length) locucion.current = { valores, indice: 0 };
    });
  }, []);

  useEffect(() => {
    let temporizador: number | undefined;
    let fase = 0;

    const pintar = () => {
      fase += 1;
      const actual = estadoRef.current;
      let fuerza = 0;

      if (actual === 'hablando') {
        const loc = locucion.current;
        if (loc && loc.indice < loc.valores.length) {
          fuerza = loc.valores[loc.indice]!;
          loc.indice += 1;
        } else {
          // Hablando sin envolvente (TTS que aun no ha llegado): un vaiven
          // suave, para no quedarse plano en mitad de una frase.
          fuerza = 0.35 + 0.15 * Math.sin(fase / 3);
        }
      } else if (actual === 'escuchando') {
        fuerza = Math.min(1, nivelRef.current * 1.4);
      } else if (actual === 'pensando') {
        // Latido lento: esta trabajando, no colgado.
        fuerza = 0.18 + 0.12 * Math.sin(fase / 6);
      } else {
        fuerza = 0.05 + 0.02 * Math.sin(fase / 12);
      }

      setAlturas((previas) =>
        PESOS.map((peso, i) => {
          // Cada barra lleva su propio retardo: la onda "viaja" del centro a
          // los lados en vez de subir todas a la vez como un bloque.
          const desfase = Math.sin(fase / 2.5 + i * 0.55) * 0.18;
          const objetivo = Math.max(0.05, Math.min(1, fuerza * peso * (1 + desfase)));
          // Suavizado: sube rapido (se nota el ataque de la voz) y baja lento.
          const anterior = previas[i] ?? 0.06;
          const suavizado = objetivo > anterior ? 0.55 : 0.2;
          return anterior + (objetivo - anterior) * suavizado;
        }),
      );

      temporizador = window.setTimeout(pintar, 1000 / FPS);
    };

    pintar();
    return () => {
      if (temporizador) window.clearTimeout(temporizador);
    };
  }, []);

  const rotulo =
    estado === 'hablando'
      ? 'Le estoy contando'
      : estado === 'escuchando'
        ? 'Le escucho'
        : estado === 'pensando'
          ? 'Lo estoy mirando'
          : 'Aquí estoy';

  const color =
    estado === 'hablando'
      ? 'bg-accent'
      : estado === 'escuchando'
        ? 'bg-[hsl(var(--cobro))]'
        : 'bg-muted-foreground/60';

  return (
    <section
      className={cn(
        'flex flex-col items-center justify-center gap-4 rounded-lg border border-border/70 bg-card/60 p-6',
        className,
      )}
      aria-label="Androcles"
    >
      <div className="flex h-28 w-full items-center justify-center gap-1.5" aria-hidden="true">
        {alturas.map((altura, i) => (
          <span
            key={i}
            className={cn('w-2 rounded-full transition-colors sm:w-2.5', color)}
            style={{ height: `${Math.round(altura * 100)}%` }}
          />
        ))}
      </div>

      {/* El rotulo es la parte accesible: el color por si solo no basta, y el
          jefe tiene que saber que pasa sin interpretar una animacion. */}
      <p
        className={cn(
          'text-base font-semibold',
          estado === 'hablando'
            ? 'text-accent'
            : estado === 'escuchando'
              ? 'text-[hsl(var(--cobro))]'
              : 'text-muted-foreground',
        )}
        aria-live="polite"
      >
        {rotulo}
      </p>
    </section>
  );
}
